// Phase 3B — the earned invitation.
//
// Only rendered after the visitor has actually received value, capped to once
// per session and once per cooldown window. Inline, never modal, never
// covering content, always dismissible.
import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowRight, X } from "lucide-react";
import { useReturnLoop } from "@/hooks/use-return-loop";
import { track } from "@/lib/analytics";

interface Props {
  /** Where the prompt is rendered — recorded for measurement only. */
  surface: string;
}

export function ReturnPrompt({ surface }: Props) {
  const loop = useReturnLoop();
  const decision = loop.promptDecision;
  const show = loop.ready && decision.show;

  useEffect(() => {
    if (!show) return;
    loop.markPromptShown();
    track("return_prompt_view", { surface, ask: decision.show ? decision.ask : "" });
    // Fires once when the prompt becomes eligible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show || !decision.show) return null;

  const isMembership = decision.ask === "membership";
  const to = isMembership ? "/membership" : "/auth";
  const heading = isMembership
    ? "Keep the whole path open"
    : "Keep your place across days";
  const body = isMembership
    ? "Membership opens the full library, the Academy, and every activation as it is published."
    : "Create a free account and your gates, activations, and readings stay where you left them — on any device.";

  return (
    <aside
      aria-label="Invitation"
      className="mt-16 rounded-2xl border hairline bg-charcoal/40 p-6 sm:p-8"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Because you practised</p>
          <h2 className="mt-2 font-serif text-2xl">{heading}</h2>
          <p className="mt-3 max-w-prose text-sm text-muted-foreground">{body}</p>
        </div>
        <button
          type="button"
          aria-label="Dismiss invitation"
          onClick={() => {
            loop.markPromptDismissed();
            track("return_prompt_dismiss", { surface, ask: decision.ask });
          }}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      <Link
        to={to}
        onClick={() => track("return_prompt_click", { surface, ask: decision.ask })}
        className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-full bg-gold-gradient px-6 text-sm font-semibold text-obsidian transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
      >
        {isMembership ? "See membership" : "Create a free account"}
        <ArrowRight size={14} aria-hidden />
      </Link>
    </aside>
  );
}
